
import { ITender } from "@/interfaces/tender";
import { defineComponent, PropType } from "vue";
import { mapActions, mapGetters } from "vuex";
import { EventType, trackEvent } from "@/helpers/AmplitudeHelper";
import { formatTenderName } from "@/helpers/FormatStringHelper";
import { isProEnabled } from "@/helpers/FeatureFlagHelper";
import { isExpiredTrialTariff } from "@/helpers/TariffHelper";
import * as amplitude from '@amplitude/analytics-browser';

export default defineComponent({
  name: "TenderCard",
  props: {
    tender: {
      type: Object as PropType<ITender>,
      required: true,
    },
  },
  emits: ["remove-favourite", "add-favourite"],
  data() {
    return {
      sourceTranslator: {
        mc: "tender.mc.uz",
        xarid_uzex: "xarid.uzex.uz",
        etender: "etender.uzex.uz",
        xt_xarid: "hayotbirja.uz",
        beeline: "beeline.uz",
        tenderweek: "tenderweek.com",
      } as { [key: string]: string },
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    isFavourite() {
      return !!this.tender.fav_id
    },
    shouldShowPaywall() {
      return this.$store.getters.isAuthenticated && isExpiredTrialTariff(
        this.$store.getters.getTariffCode,
        this.$store.getters.getTariffSubscriptionStatus,
      )
    },
    paywallClickOptions() {
      return {
        enabled: this.$store.getters.isAuthenticated && isExpiredTrialTariff(
          this.$store.getters.getTariffCode,
          this.$store.getters.getTariffSubscriptionStatus,
        ),
        trigger: "click",
        placement: "top-start",
      }
    },
    sourceUrl() {
      let url = this.tender.url
      if (url.startsWith("https://xt-xarid.uz/")) {
        url = url.replace("xt-xarid", "hayotbirja")
      }
      return url
    },
  },
  methods: {
    ...mapActions(['addFavourite', 'deleteFavourite', 'toggleAuthModal', 'setIntendedRoute']),
    formatName(tender: ITender) {
      return formatTenderName(tender)
    },
    formatDate(endDate: string | null) {
      if (endDate === null) {
        return "–"
      }
      const date = new Date(endDate);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      switch (this.$t("tender_details.at")) {
        case "в":
          return `${day}.${month}.${year} в ${hours}:${minutes}`;
        case "at":
          return `${day}.${month}.${year} at ${hours}:${minutes}`;
        case "да":
          return `${day}.${month}.${year} ${hours}:${minutes} да`;
      }
    },
    async toggleFavourite() {
      if (this.isFavourite) {
        const result = await this.deleteFavourite({ 
          tenderId: this.tender.id, 
          favId: this.tender.fav_id, 
          notificationKeypath: 'notifications.delete_favourite'
        })

        if (result) {
          const identifyEvent = new amplitude.Identify();
          identifyEvent.add('favourites', -1);
          amplitude.identify(identifyEvent);
          trackEvent(EventType.purchase_card_delete, { purchase_id: this.tender.id })
        }
      } else {
        const result = await this.addFavourite({ 
          tenderId: this.tender.id, 
          notificationKeypath: 'notifications.add_favourite'
        })

        if (result) {
          const identifyEvent = new amplitude.Identify();
          identifyEvent.add('favourites', 1);
          amplitude.identify(identifyEvent);
          trackEvent(EventType.purchase_card_save, { logged_in: this.isAuthenticated, purchase_id: this.tender.id })
        }
      }
    },
    handleClickTender(event: MouseEvent) {
      trackEvent(EventType.purchase_card_open, { logged_in: this.isAuthenticated, purchase_id: this.tender.id })
      if (window.innerWidth > 768) {
        // Open the link in a new tab for desktop users
        const url = '/procedures/' + this.tender.id;
        window.open(url, '_blank');
        event.preventDefault();
      }
    },
    handleClickSource(event: MouseEvent) {
      trackEvent(EventType.purchase_card_source, { logged_in: this.isAuthenticated, purchase_id: this.tender.id })
      if (isProEnabled && !this.isAuthenticated) {
        event.preventDefault()
        this.toggleAuthModal()
        return
      }
      if (this.shouldShowPaywall) {
        event.preventDefault()
      }
    },
    async handleClickFavourite() {
      if (!this.isAuthenticated) {
        trackEvent(EventType.purchase_card_save, { logged_in: this.isAuthenticated, purchase_id: this.tender.id })
        this.toggleAuthModal();
        return
      } else {
        await this.toggleFavourite();
        return
      }
    },
    handleClickCompany(event: MouseEvent) {
      const url = `/company/${this.tender.company_inn}?page=1`;
      if (window.innerWidth > 768) {
        window.open(url, '_blank');
        event.preventDefault();
      } else {
        this.$router.push({ path: url, query: {page: 1} });
        event.preventDefault();
      }
    }
  }
});
